import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getUserSettings, updateUserSettings } from '@/lib/settings';
import type { UserSettings } from '@/types';

interface SettingsContextType {
  settings: UserSettings;
  updateSettings: (newSettings: Partial<UserSettings>) => Promise<void>;
  loading: boolean;
}

const DEFAULT_SETTINGS: UserSettings = {
  currency: 'USD',
  language: 'en',
  theme: 'system',
  yearlyGoal: 0,
  defaultMonthlySalary: 0,
};

const SettingsContext = createContext<SettingsContextType>({
  settings: DEFAULT_SETTINGS,
  updateSettings: async () => {},
  loading: true,
});

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadSettings() {
    if (!user) {
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return;
    }

    try {
      const userSettings = await getUserSettings(user.uid);
      setSettings(userSettings);
      applyTheme(userSettings.theme);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateSettings(newSettings: Partial<UserSettings>) {
    if (!user) return;

    try {
      await updateUserSettings(user.uid, newSettings);
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      
      if (newSettings.theme) {
        applyTheme(newSettings.theme);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  function applyTheme(theme: 'dark' | 'light' | 'system') {
    const root = window.document.documentElement;
    
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.toggle('dark', systemTheme === 'dark');
    } else {
      root.classList.toggle('dark', theme === 'dark');
    }
  }

  return (
    <SettingsContext.Provider value={{ settings, updateSettings: handleUpdateSettings, loading }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}